// Blackjack (user decisions 2026-10-01, blackjack-suunnitelma.md): the rules shared by the server (the
// dealer and the settlement) and the table page (what the buttons allow, the totals shown).
// A card 0–51 as in Valepaskahousu: rank = 2 + (card % 13) (11–13 = J Q K, 14 = ace), suit = floor(card / 13).

export type Card = number;
export const rankOf = (c: Card) => 2 + (c % 13);
export const suitOf = (c: Card) => Math.floor(c / 13);

/** Areena has at most 4 blackjack tables, each with 5 seats (user decision 2026-10-01). */
export const BJ_MAX_TABLES = 4;
export const BJ_SEATS = 5;
/** Splitting again up to 4 hands; aces are not split again. */
export const BJ_MAX_HANDS = 4;
/** Chip values (user decision 2026-10-01). */
export const BJ_CHIPS = [5, 10, 25, 100] as const;
export const BJ_BET_MS = 20_000;
export const BJ_TURN_MS = 20_000;
export const BJ_INSURANCE_MS = 10_000;

export interface BjConfig {
  minBet: number;
  maxBet: number;
  /** Decks in the shoe. */
  decks: number;
  /** The shoe is shuffled when this share of it has been dealt. */
  penetration: number;
}
export const DEFAULT_BJ_CONFIG: BjConfig = { minBet: 5, maxBet: 200, decks: 6, penetration: 0.75 };

/** Points of one card: 2–10, face cards 10, the ace 11 (counted as 1 when needed). */
export function cardPoints(c: Card): number {
  const r = rankOf(c);
  return r === 14 ? 11 : r >= 10 ? 10 : r;
}

/** The best total; soft = an ace still counts as 11. */
export function handValue(cards: readonly Card[]): { total: number; soft: boolean } {
  let total = 0;
  let aces = 0;
  for (const c of cards) {
    total += cardPoints(c);
    if (rankOf(c) === 14) aces++;
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces--;
  }
  return { total, soft: aces > 0 };
}

/** Two cards making 21 – not after a split (a split hand's 21 is just 21). */
export const isBlackjack = (cards: readonly Card[], fromSplit = false) => !fromSplit && cards.length === 2 && handValue(cards).total === 21;
export const isBust = (cards: readonly Card[]) => handValue(cards).total > 21;

/** A fresh shoe, shuffled (Fisher–Yates) with the given random source. */
export function newShoe(decks: number, rng: (n: number) => number): Card[] {
  const shoe: Card[] = [];
  for (let d = 0; d < decks; d++) for (let c = 0; c < 52; c++) shoe.push(c);
  for (let i = shoe.length - 1; i > 0; i--) {
    const j = rng(i + 1);
    [shoe[i], shoe[j]] = [shoe[j]!, shoe[i]!];
  }
  return shoe;
}

export interface Hand {
  cards: Card[];
  bet: number;
  doubled: boolean;
  /** Made by splitting (no blackjack, no surrender of the rule "21 is blackjack"). */
  fromSplit: boolean;
  /** A split ace: one card only, no further play. */
  splitAce: boolean;
  /** The player is done with this hand (stood, bust, 21, doubled, split ace). */
  done: boolean;
}

export const newHand = (bet: number, cards: Card[] = []): Hand => ({ cards, bet, doubled: false, fromSplit: false, splitAce: false, done: false });

/** The pair rule: the same rank (10 J Q K are different ranks – a pair means the same card). */
export const isPair = (h: Hand) => h.cards.length === 2 && rankOf(h.cards[0]!) === rankOf(h.cards[1]!);

export const canHit = (h: Hand) => !h.done && !h.splitAce && handValue(h.cards).total < 21;
/** Doubling on the first two cards, also after a split (user decision 2026-10-01). */
export const canDouble = (h: Hand, balance: number) => !h.done && !h.splitAce && h.cards.length === 2 && balance >= h.bet;
/** Splitting a pair, up to 4 hands; split aces are not split again. */
export const canSplit = (hands: readonly Hand[], h: Hand, balance: number) =>
  !h.done && isPair(h) && hands.length < BJ_MAX_HANDS && !(h.splitAce || (h.fromSplit && rankOf(h.cards[0]!) === 14)) && balance >= h.bet;

/** The dealer draws to 16 and stands on every 17 (user decision 2026-10-01: soft 17 too). */
export const dealerDraws = (cards: readonly Card[]) => handValue(cards).total < 17;

/** The dealer peeks for a blackjack when the up-card is an ace or worth ten. */
export const dealerPeeks = (up: Card) => cardPoints(up) >= 10;

export type HandResult = 'blackjack' | 'win' | 'push' | 'lose' | 'bust';

/**
 * The settlement of one hand: what is paid back (the bet included). Blackjack 3:2 (rounded down to whole
 * AreCoins), a win 1:1, a push returns the bet. A player blackjack against a dealer blackjack is a push.
 */
export function settle(h: Hand, dealer: readonly Card[]): { result: HandResult; payout: number } {
  const p = handValue(h.cards).total;
  const d = handValue(dealer).total;
  const playerBj = isBlackjack(h.cards, h.fromSplit);
  const dealerBj = isBlackjack(dealer);
  if (p > 21) return { result: 'bust', payout: 0 };
  if (playerBj && dealerBj) return { result: 'push', payout: h.bet };
  if (playerBj) return { result: 'blackjack', payout: h.bet + Math.floor((h.bet * 3) / 2) };
  if (dealerBj) return { result: 'lose', payout: 0 };
  if (d > 21 || p > d) return { result: 'win', payout: h.bet * 2 };
  if (p === d) return { result: 'push', payout: h.bet };
  return { result: 'lose', payout: 0 };
}

/** Insurance (half the bet at most) pays 2:1 when the dealer has a blackjack. */
export const insuranceMax = (bet: number) => Math.floor(bet / 2);
export const insurancePayout = (insurance: number, dealer: readonly Card[]) => (isBlackjack(dealer) ? insurance * 3 : 0);

const SUIT_SIGNS = ['♠', '♥', '♦', '♣'];
export const cardLabel = (c: Card) => `${['J', 'Q', 'K', 'A'][rankOf(c) - 11] ?? String(rankOf(c))}${SUIT_SIGNS[suitOf(c)]}`;
